import {
	Activity,
	AlertCircle,
	AlertTriangle,
	Archive,
	BarChart01,
	BarChart02,
	Bell01,
	Briefcase02,
	Building01,
	Building07,
	Calendar,
	CalendarDate,
	CheckCircle,
	CheckCircleBroken,
	Circle,
	Clipboard,
	ClipboardCheck,
	ClockStopwatch,
	Code01,
	Cube01,
	Database01,
	Dataflow01,
	Dataflow03,
	DotsGrid,
	Edit01,
	File05,
	File06,
	GitMerge,
	Hash02,
	HelpCircle,
	Inbox01,
	InfoCircle,
	IntersectCircle,
	Laptop02,
	LayersThree01,
	LayersTwo01,
	LineChartDown05,
	LineChartUp05,
	List,
	Loading01,
	Loading02,
	Loading03,
	Lock02,
	Lock03,
	Mail01,
	Map01,
	MarkerPin01,
	MarkerPin04,
	MessageAlertSquare,
	MinusCircle,
	Monitor01,
	PackageCheck,
	PauseCircle,
	Pentagon,
	Phone,
	PlusCircle,
	PuzzlePiece01,
	RefreshCw01,
	Shield01,
	ShieldTick,
	Signal01,
	SlashCircle01,
	Sliders04,
	Tag01,
	Target05,
	Ticket01,
	Ticket02,
	Triangle,
	User02,
	UserCheck01,
	Users01,
	XCircle,
	Zap,
} from "@untitledui/icons";
import type { FC } from "react";

export type IconEntry = {
	name: string;
	component: FC<{ className?: string; size?: number }>;
};

/**
 * Master icon catalog — shared by the IconPickerPopover and any other
 * component that needs to resolve an icon name string to a React component.
 */
export const ICON_CATALOG: IconEntry[] = [
	// Tickets / documents
	{ name: "Ticket01", component: Ticket01 },
	{ name: "Ticket02", component: Ticket02 },
	{ name: "Clipboard", component: Clipboard },
	{ name: "File06", component: File06 },
	{ name: "File05", component: File05 },
	{ name: "ClipboardCheck", component: ClipboardCheck },
	{ name: "Edit01", component: Edit01 },

	// Data / infrastructure
	{ name: "Database01", component: Database01 },
	{ name: "Sliders04", component: Sliders04 },
	{ name: "Map01", component: Map01 },
	{ name: "MarkerPin01", component: MarkerPin01 },
	{ name: "MarkerPin04", component: MarkerPin04 },
	{ name: "Pentagon", component: Pentagon },
	{ name: "Triangle", component: Triangle },

	// Buildings / people
	{ name: "Building01", component: Building01 },
	{ name: "Building07", component: Building07 },
	{ name: "User02", component: User02 },
	{ name: "Users01", component: Users01 },
	{ name: "UserCheck01", component: UserCheck01 },
	{ name: "Shield01", component: Shield01 },

	// Comms / time
	{ name: "ShieldTick", component: ShieldTick },
	{ name: "Inbox01", component: Inbox01 },
	{ name: "Mail01", component: Mail01 },
	{ name: "Phone", component: Phone },
	{ name: "Calendar", component: Calendar },
	{ name: "CalendarDate", component: CalendarDate },

	// Alerts / info
	{ name: "ClockStopwatch", component: ClockStopwatch },
	{ name: "AlertCircle", component: AlertCircle },
	{ name: "AlertTriangle", component: AlertTriangle },
	{ name: "InfoCircle", component: InfoCircle },
	{ name: "MessageAlertSquare", component: MessageAlertSquare },
	{ name: "Tag01", component: Tag01 },

	// Dev / flow
	{ name: "Hash02", component: Hash02 },
	{ name: "Code01", component: Code01 },
	{ name: "GitMerge", component: GitMerge },
	{ name: "Dataflow01", component: Dataflow01 },
	{ name: "Dataflow03", component: Dataflow03 },
	{ name: "Lock03", component: Lock03 },

	// Misc
	{ name: "Lock02", component: Lock02 },
	{ name: "PackageCheck", component: PackageCheck },
	{ name: "PuzzlePiece01", component: PuzzlePiece01 },
	{ name: "Signal01", component: Signal01 },
	{ name: "BarChart01", component: BarChart01 },
	{ name: "BarChart02", component: BarChart02 },
	{ name: "Cube01", component: Cube01 },

	// Charts / activity
	{ name: "LineChartDown05", component: LineChartDown05 },
	{ name: "LineChartUp05", component: LineChartUp05 },
	{ name: "Zap", component: Zap },
	{ name: "Activity", component: Activity },

	// Status / circle family — common for lifecycle statuses
	{ name: "Circle", component: Circle },
	{ name: "CheckCircle", component: CheckCircle },
	{ name: "CheckCircleBroken", component: CheckCircleBroken },
	{ name: "XCircle", component: XCircle },
	{ name: "PlusCircle", component: PlusCircle },
	{ name: "MinusCircle", component: MinusCircle },
	{ name: "PauseCircle", component: PauseCircle },
	{ name: "SlashCircle01", component: SlashCircle01 },
	{ name: "HelpCircle", component: HelpCircle },
	{ name: "AlertCircle", component: AlertCircle },
	{ name: "IntersectCircle", component: IntersectCircle },

	// Loading / progress
	{ name: "Loading01", component: Loading01 },
	{ name: "Loading02", component: Loading02 },
	{ name: "Loading03", component: Loading03 },
	{ name: "RefreshCw01", component: RefreshCw01 },

	// Archive / hardware
	{ name: "Archive", component: Archive },
	{ name: "Laptop02", component: Laptop02 },
	{ name: "Monitor01", component: Monitor01 },
	{ name: "Bell01", component: Bell01 },
	{ name: "Target05", component: Target05 },
	{ name: "Briefcase02", component: Briefcase02 },

	// Layout
	{ name: "LayersThree01", component: LayersThree01 },
	{ name: "LayersTwo01", component: LayersTwo01 },
	{ name: "List", component: List },
	{ name: "DotsGrid", component: DotsGrid },
];

/**
 * Case-insensitive lookup map: icon name → React component.
 * Built once at module load time.
 */
const _iconMap = new Map<string, FC<{ className?: string; size?: number }>>(
	ICON_CATALOG.map((e) => [e.name.toLowerCase(), e.component]),
);

/**
 * Resolve an icon name string (from the API) to the corresponding React
 * component. Returns `null` when the name is unknown.
 */
export function resolveIconComponent(
	name: string | null | undefined,
): FC<{ className?: string; size?: number }> | null {
	if (!name) return null;
	return _iconMap.get(name.toLowerCase()) ?? null;
}
